import React, { useMemo } from 'react';

import { BottomTabBarProps, createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { ParamListBase } from '@react-navigation/native';
import { TabNavigationState } from '@react-navigation/native';
import { EventArg } from '@react-navigation/native';

import { PicksTabIcon } from '@/assets/icons/tab/PicksTabIcon';
import { LobbyIcon } from '@/assets/icons/tab/home';
import ResultsIcon from '@/assets/icons/tab/results';
import { useHandleSocialLink } from '@/feature/entry-share/hooks/use-share-link-import';
import { PlayGameBar } from '@/feature/game-details-arcade/components/PlayBar';
import { usePrefetchGeotoken } from '@/feature/lobby-arcade/hooks/use-prefetch-geotoken';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { ArcadeResultsScreen } from '@/feature/results-arcade/screens/ArcadeResults';
import { useAppInit } from '@/hooks/use-app-init';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useOneLinkNavigation } from '@/hooks/use-one-link-navigation';

import { AccountTabConfig, PromosTabConfig, TabBar, TabOptions, mapTabsFromTabSettings } from '../tabs';
import { LobbyStack } from './lobbyStack';
import { ArcadeTabNavigatorParamList } from './types';

const TabArcade = createBottomTabNavigator<ArcadeTabNavigatorParamList>();

const ArcadeTabsConfig = [
    { name: 'ArcadeLobby', component: LobbyStack, icon: LobbyIcon, title: 'Home' },
    {
        name: 'ArcadeResults',
        component: ArcadeResultsScreen,
        icon: ResultsIcon,
        title: 'Results',
    },
] as const;

const EmptyScreen = () => null;

const ProductSwitcherTab = {
    name: 'SwitchToPicks',
    component: EmptyScreen,
    icon: () => <PicksTabIcon />,
    title: 'Picks',
    listeners: () => ({
        tabPress: (e: EventArg<'tabPress', true>) => {
            // Prevent default navigation to the tab's screen
            e.preventDefault();
            useProductTransitionStore.getState().actions.triggerTransition(Product.Pickem);
        },
    }),
} as const;

export const ArcadeTabsNavigator = () => {
    useOneLinkNavigation();
    useHandleSocialLink(Product.Arcade);

    const tabSettings = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.tabSettings);

    const showPromosTab = useShouldShowPromosTab();

    const tabsConfig = useMemo(() => {
        return mapTabsFromTabSettings(
            [...ArcadeTabsConfig, showPromosTab ? PromosTabConfig : ProductSwitcherTab, AccountTabConfig].filter(
                Boolean
            ),
            tabSettings
        );
    }, [tabSettings, showPromosTab]);

    const { geocomplyClientReady } = useAppInit();
    usePrefetchGeotoken(geocomplyClientReady);

    return (
        <TabArcade.Navigator tabBar={ArcadeTabBar} screenOptions={TabOptions}>
            {tabsConfig.map(it => (
                <TabArcade.Screen key={it.name} {...it} options={{ tabBarIcon: it.icon, title: it.title }} />
            ))}
        </TabArcade.Navigator>
    );
};

const isGameDetailsScreen = (state: TabNavigationState<ParamListBase>) => {
    const route = state.routes[state.index];
    const screenName = route.state?.routes[route.state?.index ?? 0].name;
    return screenName === 'ArcadeGameDetailsScreen';
};

const ArcadeTabBar = (props: BottomTabBarProps) => {
    const { state } = props;
    return (
        <>
            {isGameDetailsScreen(state) ? <PlayGameBar /> : null}
            <TabBar {...props} />
        </>
    );
};

/**
 * Determines if we should render the promos tab on Arcade.
 * If 'Pickem' product is available, we do not render the promos tab,
 * but instead we render the Pickem switch tab.
 */
export const useShouldShowPromosTab = () => {
    const { isProductAvailable } = useAvailableProducts();
    const isPickemAvailable = isProductAvailable(Product.Pickem);
    return !isPickemAvailable;
};
